/**
 * Utility functions for image processing and compression.
 */

/**
 * Format bytes into human-readable string (e.g. 240 KB, 1.2 MB).
 */
export const formatFileSize = (bytes) => {
  if (!bytes || bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
};

/**
 * Compresses an image file using an HTML5 Canvas.
 * Resizes the image to fit within maxWidth x maxHeight and compresses to JPEG format.
 *
 * @param {File} file - Original file
 * @param {Object} options
 * @param {number} [options.maxWidth=1600] - Max width allowed
 * @param {number} [options.maxHeight=1600] - Max height allowed
 * @param {number} [options.quality=0.75] - JPEG compression quality (0 to 1)
 * @returns {Promise<{ file: File, originalSize: number, compressedSize: number }>}
 */
export const compressImage = (file, options = {}) => {
  const { maxWidth = 1600, maxHeight = 1600, quality = 0.75 } = options;

  return new Promise((resolve) => {
    // Only process standard image types (exclude SVGs or non-images)
    if (!file || !file.type || !file.type.startsWith('image/') || file.type === 'image/svg+xml') {
      return resolve({
        file,
        originalSize: file?.size || 0,
        compressedSize: file?.size || 0
      });
    }

    const reader = new FileReader();
    reader.onerror = () => {
      resolve({ file, originalSize: file.size, compressedSize: file.size });
    };

    reader.onload = (e) => {
      const img = new Image();
      img.onerror = () => {
        resolve({ file, originalSize: file.size, compressedSize: file.size });
      };

      img.onload = () => {
        try {
          let { width, height } = img;

          // Compute target dimensions keeping aspect ratio
          if (width > maxWidth || height > maxHeight) {
            if (width / height > maxWidth / maxHeight) {
              height = Math.round((height * maxWidth) / width);
              width = maxWidth;
            } else {
              width = Math.round((width * maxHeight) / height);
              height = maxHeight;
            }
          }

          const canvas = document.createElement('canvas');
          canvas.width = width;
          canvas.height = height;

          const ctx = canvas.getContext('2d');
          if (!ctx) {
            return resolve({ file, originalSize: file.size, compressedSize: file.size });
          }

          // Fill white background in case of transparent PNG converted to JPEG
          ctx.fillStyle = '#FFFFFF';
          ctx.fillRect(0, 0, width, height);
          ctx.drawImage(img, 0, 0, width, height);

          canvas.toBlob(
            (blob) => {
              if (!blob) {
                return resolve({ file, originalSize: file.size, compressedSize: file.size });
              }

              // Determine filename with .jpg extension
              let newFileName = file.name;
              const lastDot = newFileName.lastIndexOf('.');
              if (lastDot !== -1) {
                newFileName = newFileName.substring(0, lastDot) + '.jpg';
              } else {
                newFileName += '.jpg';
              }

              // If the compressed size is larger than original and dimensions didn't shrink,
              // keep original file
              if (blob.size >= file.size && width === img.width && height === img.height) {
                return resolve({
                  file,
                  originalSize: file.size,
                  compressedSize: file.size
                });
              }

              const compressedFile = new File([blob], newFileName, {
                type: 'image/jpeg',
                lastModified: Date.now()
              });

              resolve({
                file: compressedFile,
                originalSize: file.size,
                compressedSize: compressedFile.size
              });
            },
            'image/jpeg',
            quality
          );
        } catch (err) {
          console.warn('Canvas compression error:', err);
          resolve({ file, originalSize: file.size, compressedSize: file.size });
        }
      };

      img.src = e.target.result;
    };

    reader.readAsDataURL(file);
  });
};
